import { PaperAirplaneIcon } from '@heroicons/react/24/solid'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

// Demo-Chat fuer die Launcher-Showcases, ohne Backend.
// Die Vorschlaege liefern Beispielantworten, Freitext bekommt einen ehrlichen Demo-Hinweis.

const ROWS = [
  { n: 'Lena K.', v: 92, c: '#43DBBD' },
  { n: 'Jonas M.', v: 87, c: '#FFB628' },
  { n: 'Aylin S.', v: 81, c: '#FE79A7' }
]

const REPLIES = {
  'Top-Matches zeigen': {
    text: 'Das sind die stärksten Matches für Sales Lead.',
    rows: ROWS
  },
  'Ergebnisse erklären': {
    text: 'Lena liegt in 7 von 8 Dimensionen im Zielkorridor eures Sales-Profils. Am stärksten ist sie bei Kommunikation und Durchsetzungsvermögen, etwas unter dem Profil bei Detailorientierung.'
  },
  'Interviewfragen vorschlagen': {
    text: 'Drei Fragen für das Gespräch mit Lena, abgeleitet aus ihrem Profil.',
    list: [
      'Erzähl von einem Deal, bei dem du gegen Widerstand hartnäckig geblieben bist.',
      'Wie stellst du sicher, dass in Angeboten keine Details untergehen?',
      'Wann hast du zuletzt deine Meinung nach einem Kundengespräch geändert?'
    ]
  }
}
const SUGGESTIONS = Object.keys(REPLIES)
const FALLBACK = {
  text: 'Das ist eine Demo, frei antworten kann ich hier noch nicht. Probier einen der Vorschläge unten.'
}

const Bubble = ({ from, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ type: 'spring', stiffness: 420, damping: 30 }}
    className={
      from === 'user'
        ? 'ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-[#407BFF] px-3.5 py-2.5 text-sm leading-normal text-white'
        : 'max-w-[90%] rounded-2xl rounded-tl-md bg-[#F6F7F9] px-3.5 py-2.5 text-sm leading-normal text-gray-800'
    }
  >
    {children}
  </motion.div>
)

Bubble.propTypes = { from: PropTypes.string, children: PropTypes.node }

const MatchRows = ({ rows }) => (
  <div className='mt-3 flex flex-col gap-2.5'>
    {rows.map((r, i) => (
      <div key={r.n} className='flex items-center gap-2.5'>
        <span
          className='flex h-7 w-7 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-gray-800'
          style={{ background: r.c }}
        >
          {r.n
            .split(' ')
            .map((p) => p[0])
            .join('')}
        </span>
        <span className='w-16 text-sm font-medium'>{r.n}</span>
        <span className='h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200'>
          <motion.span
            className='block h-full rounded-full bg-[#407BFF]'
            initial={{ width: 0 }}
            animate={{ width: `${r.v}%` }}
            transition={{ delay: 0.15 + i * 0.12, duration: 0.8 }}
          />
        </span>
        <span className='w-10 text-right text-sm font-semibold'>{r.v} %</span>
      </div>
    ))}
  </div>
)

MatchRows.propTypes = { rows: PropTypes.array }

const DemoChat = ({ onThinkingChange }) => {
  const [messages, setMessages] = useState([
    {
      from: 'assistant',
      text: 'Hi! 3 Kandidat:innen haben heute ihr Assessment abgeschlossen. Womit soll ich anfangen?'
    }
  ])
  const [typing, setTyping] = useState(false)
  const [input, setInput] = useState('')
  const listRef = useRef(null)

  useEffect(() => {
    onThinkingChange && onThinkingChange(typing)
  }, [typing, onThinkingChange])

  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages, typing])

  const send = (text) => {
    const q = text.trim()
    if (!q || typing) return
    setMessages((m) => [...m, { from: 'user', text: q }])
    setInput('')
    setTyping(true)
    setTimeout(() => {
      setTyping(false)
      setMessages((m) => [
        ...m,
        { from: 'assistant', ...(REPLIES[q] || FALLBACK) }
      ])
    }, 1600)
  }

  return (
    <>
      <div
        ref={listRef}
        className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4'
      >
        {messages.map((m, i) => (
          <Bubble key={i} from={m.from}>
            {m.text}
            {m.rows && <MatchRows rows={m.rows} />}
            {m.list && (
              <ol className='mt-2 list-decimal space-y-1.5 pl-4'>
                {m.list.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ol>
            )}
          </Bubble>
        ))}
        {typing && (
          <div className='flex w-14 gap-1 rounded-2xl rounded-tl-md bg-[#F6F7F9] px-3.5 py-3.5'>
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className='h-1.5 w-1.5 rounded-full bg-gray-400'
                animate={{ y: [0, -3, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 0.7,
                  delay: i * 0.12
                }}
              />
            ))}
          </div>
        )}
      </div>
      <div className='border-t border-gray-100 px-3 pb-3 pt-2.5'>
        <div className='mb-2.5 flex gap-1.5 overflow-x-auto pb-0.5'>
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type='button'
              onClick={() => send(s)}
              className='flex-none whitespace-nowrap rounded-full border border-[#407BFF] px-3 py-1.5 text-xs font-medium text-[#407BFF] hover:bg-blue-50'
            >
              {s}
            </button>
          ))}
        </div>
        <form
          className='flex items-center gap-2 rounded-xl border border-gray-200 py-1 pl-3 pr-1 focus-within:border-[#407BFF]'
          onSubmit={(e) => {
            e.preventDefault()
            send(input)
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='Frag den Assistenten …'
            className='min-w-0 flex-1 border-0 bg-transparent p-0 py-1.5 text-sm focus:ring-0'
          />
          <button
            type='submit'
            aria-label='Senden'
            disabled={!input.trim()}
            className='rounded-lg bg-[#407BFF] p-2 text-white disabled:opacity-40'
          >
            <PaperAirplaneIcon className='h-4 w-4' />
          </button>
        </form>
      </div>
    </>
  )
}

DemoChat.propTypes = { onThinkingChange: PropTypes.func }

export default DemoChat
